<template>
	<div>
		<div :class="['card flex justify-content-center', props.customClass]">
			<template v-if="props.type === 'static'">
				<transition
					name="fade-slide"
					mode="out-in"
					@after-leave="handleAfterLeave"
					@after-enter="handleAfterEnter"
				>
					<Message
						v-if="!isClosing && isShowAlert"
						:severity="props.severity"
						closable
						class="tv__alert"
						style="width: 100%"
						@close="handleClose"
					>
						<span v-if="props.title" class="p-message-title">{{
							props.title
						}}</span>
						<span
							v-if="props.detail"
							class="p-message-detail"
							v-html="props.detail"
						></span>
					</Message>
				</transition>
			</template>
			<template v-else-if="props.type === 'alert'">
				<Toast
					class="tv__alert"
					:style="toastStyle"
					@close="clear"
					:severity="props.severity"
				>
					<template #message>
						<Message
							:severity="props.severity"
							:closable="false"
							style="border: none; width: 100%"
							class="tv__alert sticky-alert"
						>
							<span v-if="props.title" class="p-message-title">
								{{ props.title }}
							</span>
							<span
								v-if="props.detail"
								class="p-message-detail"
								v-html="props.detail"
							></span>
							<TVButtonBase
								v-if="props.buttonTitle"
								:value="props.buttonTitle"
								variant="outlined"
								size="sm"
								type="button"
								class="mt-3"
								@click="redirect"
							/> </Message
					></template>
				</Toast>
			</template>
		</div>
	</div>
</template>

<script setup>
import {
	onMounted,
	defineEmits,
	defineProps,
	ref,
	watch,
	watchEffect,
	computed,
} from 'vue'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import Message from 'primevue/message'
import { useDevice } from '../../src/utils/useDevice'
import TVButtonBase from '../ui/TVButtonBase.vue'

const toast = useToast()
const emit = defineEmits(['close'])
const isClosing = ref(false)
const isShowAlert = ref(true)

const props = defineProps({
	title: { type: String, default: '' },
	detail: { type: String, default: '' },
	label: { type: String, default: '' },
	timeLife: { type: [String, Number], default: 80000000 },
	modelValue: { type: String, default: '' },
	closeButton: { type: Boolean, default: true },
	buttonTitle: { type: String, default: '' },
	customClass: { type: String, default: '' },
	type: {
		type: String,
		default: 'alert',
		validator: (value) => ['static', 'alert'].includes(value),
	},
	severity: {
		type: String,
		default: 'info',
		validator: (value) =>
			['error', 'success', 'secondary', 'info', 'contrast', 'warn'].includes(
				value,
			),
	},
})
const { isMobile } = useDevice()
const toastStyle = computed(() => {
	return isMobile.value ? { width: '90%' } : {}
})

const showAlert = () => {
	const titleText = props.title.replace(/<br\s*\/?>/gi, '\n')
	const detailText = props.detail.replace(/<br\s*\/?>/gi, '\n')

	toast.add({
		severity: props.severity,
		summary: titleText,
		detail: detailText,
		life: props.timeLife === 'sticky' ? null : props.timeLife,
	})
}

const clear = () => {
	toast.removeAllGroups()
	emit('close')
}

const handleClose = () => {
	isClosing.value = true
}

const handleAfterLeave = () => {
	isShowAlert.value = false
	isClosing.value = false
	emit('close')
}
const handleAfterEnter = () => {
	isClosing.value = false
}
watch(isShowAlert, (newValue) => {
	if (newValue && props.type === 'alert' && props.detail) {
		showAlert()
	}
})

watchEffect(() => {
	if (!isShowAlert.value) {
		clear()
	}
})

onMounted(() => {
	if (props.type === 'alert' && props.detail) {
		showAlert()
	}
})

const redirect = () => {
	emit('action')
}
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
	transition: all 0.5s ease;
}

.fade-slide-enter-from {
	transform: translateX(-100%);
	opacity: 0;
}

.fade-slide-leave-to {
	transform: translateX(100%);
	opacity: 0;
}
</style>
